import { z } from "zod";

export enum HttpStatus {
	InvalidInput = 400,
	Unauthorized = 401,
	Forbidden = 403,
	NotFound = 404,
	Success = 200,
	Created = 201,
	NoContent = 204,
	InternalServerError = 500,
	Conflict = 409,
}

export const InvalidInputResponse = z.object({
	status: z.literal(HttpStatus.InvalidInput),
	body: z.object({
		message: z.string(),
	}),
});

export const NoContentResponse = z.object({
	status: z.literal(HttpStatus.NoContent),
	body: z.void(),
});

export const NotFoundResponse = z.object({
	status: z.literal(HttpStatus.NotFound),
	body: z.object({
		message: z.string(),
	}),
});

export const InternalServerErrorResponse = z.object({
	status: z.literal(HttpStatus.InternalServerError),
	body: z.object({
		message: z.string(),
	}),
});

export const UnauthorizedResponse = z.object({
	status: z.literal(HttpStatus.Unauthorized),
	body: z.object({
		message: z.string(),
	}),
});

export const ForbiddenResponse = z.object({
	status: z.literal(HttpStatus.Forbidden),
	body: z.object({
		message: z.string(),
	}),
});

export const ConflictResponse = z.object({
	status: z.literal(HttpStatus.Conflict),
	body: z.object({
		message: z.string(),
	}),
});

export const CommonResponses = [
	InvalidInputResponse,
	InternalServerErrorResponse,
	UnauthorizedResponse,
	ForbiddenResponse,
	NotFoundResponse,
	ConflictResponse,
] as const;

export interface GETContract {
	path: string;
	method: "GET";
	search: z.AnyZodObject;
	params: z.AnyZodObject;
	serializePath: (
		params: z.infer<z.AnyZodObject>,
		search: z.infer<z.AnyZodObject>,
	) => string;
	response: z.ZodSchema;
}

export interface SSEContract {
	path: string;
	method: "GET";
	search: z.AnyZodObject;
	params: z.AnyZodObject;
	roles: string[];
	serializePath: (
		params: z.infer<z.AnyZodObject>,
		search: z.infer<z.AnyZodObject>,
	) => string;
	response: z.ZodSchema;
	event: z.ZodSchema;
}

export interface POSTContract {
	path: string;
	method: "POST";
	search: z.AnyZodObject;
	params: z.AnyZodObject;
	body: z.ZodSchema;
	serializePath: (
		params: z.infer<z.AnyZodObject>,
		search: z.infer<z.AnyZodObject>,
	) => string;
	response: z.ZodSchema;
}

export interface PUTContract {
	path: string;
	method: "PUT";
	search: z.AnyZodObject;
	params: z.AnyZodObject;
	body: z.ZodSchema;
	serializePath: (
		params: z.infer<z.AnyZodObject>,
		search: z.infer<z.AnyZodObject>,
	) => string;
	response: z.ZodSchema;
}

export interface PATCHContract {
	path: string;
	method: "PATCH";
	search: z.AnyZodObject;
	params: z.AnyZodObject;
	body: z.ZodSchema;
	serializePath: (
		params: z.infer<z.AnyZodObject>,
		search: z.infer<z.AnyZodObject>,
	) => string;
	response: z.ZodSchema;
}

export interface DELETEContract {
	path: string;
	method: "DELETE";
	search: z.AnyZodObject;
	params: z.AnyZodObject;
	serializePath: (
		params: z.infer<z.AnyZodObject>,
		search: z.infer<z.AnyZodObject>,
	) => string;
	response: z.ZodSchema;
}

export function buildGETContract<T extends GETContract>(contract: T) {
	return contract;
}

export function buildSSEContract<T extends SSEContract>(contract: T) {
	return contract;
}

export function buildPOSTContract<T extends POSTContract>(contract: T) {
	return contract;
}

export function buildPUTContract<T extends PUTContract>(contract: T) {
	return contract;
}

export function buildDELETEContract<T extends DELETEContract>(contract: T) {
	return contract;
}

export function buildPATCHContract<T extends PATCHContract>(contract: T) {
	return contract;
}

export function serializePathBasic(
	path: string,
	params: z.infer<z.AnyZodObject>,
	search: z.infer<z.AnyZodObject>,
) {
	let pathWithParams = path;
	for (const [key, value] of Object.entries(params)) {
		pathWithParams = pathWithParams.replace(`:${key}`, value);
	}

	const searchParams = new URLSearchParams();

	for (const [key, value] of Object.entries(search)) {
		if (Array.isArray(value)) {
			for (const v of value) {
				searchParams.append(key, v);
			}
		} else if (value !== undefined) {
			searchParams.append(key, value);
		}
	}

	const searchString = searchParams.toString();

	return `${pathWithParams}${searchString ? `?${searchString}` : ""}`;
}
